import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, ToggleLeft, ToggleRight } from "lucide-react";
import { ADMIN_TENANT_URL, ADMIN_OVERRIDES_URL, ADMIN_PLANS_URL } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";

const TRUSTEDGE_FEATURES = [
    { key: 'dashboard',              label: 'Dashboard',              group: null },
    { key: 'customers',              label: 'Customers',              group: null },
    { key: 'followup',               label: 'Follow Up',              group: null },
    { key: 'tasks',                  label: 'Tasks',                  group: null },
    { key: 'report_timeline',        label: 'Timeline',               group: 'Reports' },
    { key: 'report_lead_sources',    label: 'Lead Meta Form',         group: 'Reports' },
    { key: 'report_lead_types',      label: 'Lead Source',            group: 'Reports' },
    { key: 'report_lead_potential',  label: 'Lead Potential',         group: 'Reports' },
    { key: 'report_sales_forecast',  label: 'Sales Forecast',         group: 'Reports' },
    { key: 'report_engagement',      label: 'Engagement',             group: 'Reports' },
    { key: 'report_conversion',      label: 'Conversion',             group: 'Reports' },
    { key: 'report_email_analytics', label: 'Email Analytics',        group: 'Reports' },
    { key: 'whatsapp_campaigns',     label: 'Campaigns',              group: 'WhatsApp' },
    { key: 'whatsapp_inbox',         label: 'Inbox',                  group: 'WhatsApp' },
    { key: 'whatsapp_integration',   label: 'WA Integration',         group: 'WhatsApp' },
    { key: 'whatsapp_templates',     label: 'WA Templates',           group: 'WhatsApp' },
    { key: 'email_integration',      label: 'Email Integration',      group: 'Email' },
    { key: 'email_templates',        label: 'Email Templates',        group: 'Email' },
];

const ENGAGE_FEATURES = [
    { key: 'engage_dashboard',           label: 'Dashboard',           group: null },
    { key: 'engage_enquiries',           label: 'Patient Enquiries',   group: null },
    { key: 'engage_referring_doctors',   label: 'Referring Doctors',   group: null },
    { key: 'engage_ambulance',           label: 'Ambulance Partners',  group: null },
    { key: 'engage_corporate_accounts',  label: 'Corporate Accounts',  group: null },
    { key: 'engage_health_camps',        label: 'Health Camps',        group: null },
    { key: 'engage_digital_marketing',   label: 'Digital Marketing',   group: null },
    { key: 'engage_followups',           label: 'Follow-ups',          group: null },
    { key: 'engage_bulk_import',         label: 'Bulk Import',         group: null },
    { key: 'engage_reports',             label: 'Reports',             group: null },
    { key: 'engage_ai_caller',           label: 'AI Caller',           group: null },
    { key: 'engage_partner_portal',      label: 'Partner Portal',      group: null },
    { key: 'whatsapp_campaigns',         label: 'Campaigns',           group: 'WhatsApp' },
    { key: 'whatsapp_inbox',             label: 'Inbox',               group: 'WhatsApp' },
    { key: 'whatsapp_integration',       label: 'WA Integration',      group: 'WhatsApp' },
    { key: 'email_integration',          label: 'Email Integration',   group: 'Email' },
];

const FEATURES_BY_PRODUCT = {
    trustedge: TRUSTEDGE_FEATURES,
    engage:    ENGAGE_FEATURES,
};

const ClientDetail = () => {
    const { id }        = useParams();
    const navigate      = useNavigate();
    const { fetch, update } = useApiMethods();

    const [tenant,  setTenant]   = useState(null);
    const [plans,   setPlans]    = useState([]);
    const [features, setFeatures] = useState({});
    const [form,    setForm]     = useState({ product_type: "trustedge", plan_id: "", billing_start_date: "", billing_renewal_date: "", notes: "", is_active: true });
    const [loading,  setLoading]  = useState(false);
    const [saving,   setSaving]   = useState(false);
    const [savingFeat, setSavingFeat] = useState(false);

    const loadAll = async () => {
        setLoading(true);
        const [tr, or, pr] = await Promise.all([
            fetch(ADMIN_TENANT_URL(id),   {}, null, true),
            fetch(ADMIN_OVERRIDES_URL(id), {}, null, true),
            fetch(ADMIN_PLANS_URL,          {}, null, true),
        ]);
        if (tr?.status === "success") {
            const t = tr.data;
            setTenant(t);
            setForm({
                product_type:         t.product_type || "trustedge",
                plan_id:              t.plan?.id || "",
                billing_start_date:   t.billing_start_date || "",
                billing_renewal_date: t.billing_renewal_date || "",
                notes:                t.notes || "",
                is_active:            t.is_active,
            });
        }
        if (or?.status === "success") setFeatures(or.data || {});
        if (pr?.status === "success") setPlans(pr.data || []);
        setLoading(false);
    };

    useEffect(() => { loadAll(); }, [id]);

    const handleSave = async () => {
        setSaving(true);
        const res = await update(ADMIN_TENANT_URL(id), { ...form, id }, false, setSaving, true);
        if (res?.status === "success") { ToastMsg("success", "Client updated"); loadAll(); }
        else ToastMsg("error", res?.message || "Update failed");
        setSaving(false);
    };

    const handleSaveFeatures = async () => {
        setSavingFeat(true);
        const res = await update(ADMIN_OVERRIDES_URL(id), { features }, false, setSavingFeat, true);
        if (res?.status === "success") ToastMsg("success", "Feature overrides saved");
        else ToastMsg("error", res?.message || "Failed to save overrides");
        setSavingFeat(false);
    };

    const toggleFeature = (key) => setFeatures(f => ({ ...f, [key]: !f[key] }));

    if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading…</div>;
    if (!tenant)  return <div className="min-h-screen flex items-center justify-center text-gray-400">Not found</div>;

    const activeFeatures = FEATURES_BY_PRODUCT[form.product_type] || TRUSTEDGE_FEATURES;
    const groups         = [...new Set(activeFeatures.map(f => f.group))];
    const filteredPlans  = plans.filter(p => (p.product_type || 'trustedge') === form.product_type);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8 px-4">
            <div className="max-w-4xl mx-auto space-y-6">
                <button onClick={() => navigate("/admin/dashboard")}
                    className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                    <ArrowLeft className="w-4 h-4" /> Back
                </button>

                {/* Header */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-xl font-bold text-gray-900 dark:text-white">{tenant.org_name}</h1>
                            <p className="text-sm text-gray-500 mt-0.5">Slug: <code className="font-mono text-indigo-600">{tenant.slug}</code> · DB: <code className="font-mono text-xs text-gray-500">{tenant.db_name}</code></p>
                        </div>
                        <span className={`px-3 py-1 text-xs font-medium rounded-full ${tenant.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                            {tenant.is_active ? "Active" : "Inactive"}
                        </span>
                    </div>
                </div>

                {/* Edit Details */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                    <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">Account Details</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Product</label>
                            <select value={form.product_type} onChange={e => setForm({ ...form, product_type: e.target.value })}
                                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500/30">
                                <option value="trustedge">Gro Wing CRM</option>
                                <option value="engage">Hospital Engage</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Plan</label>
                            <select name="plan_id" value={form.plan_id} onChange={e => setForm({ ...form, plan_id: e.target.value })}
                                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500/30">
                                <option value="">— No plan —</option>
                                {filteredPlans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Status</label>
                            <button onClick={() => setForm(f => ({ ...f, is_active: !f.is_active }))}
                                className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors ${form.is_active ? "border-green-300 bg-green-50 text-green-700" : "border-red-300 bg-red-50 text-red-700"}`}>
                                {form.is_active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                                {form.is_active ? "Active" : "Inactive"}
                            </button>
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Billing Start</label>
                            <input type="date" value={form.billing_start_date} onChange={e => setForm({ ...form, billing_start_date: e.target.value })}
                                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500/30" />
                        </div>
                        <div>
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Billing Renewal</label>
                            <input type="date" value={form.billing_renewal_date} onChange={e => setForm({ ...form, billing_renewal_date: e.target.value })}
                                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500/30" />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Notes</label>
                            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
                                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500/30" />
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                        <button onClick={handleSave} disabled={saving}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg">
                            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Changes"}
                        </button>
                    </div>
                </div>

                {/* Feature Overrides */}
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Feature Access</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Overrides apply on top of the plan's defaults</p>
                        </div>
                        <button onClick={handleSaveFeatures} disabled={savingFeat}
                            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg">
                            <Save className="w-3 h-3" /> {savingFeat ? "Saving…" : "Save"}
                        </button>
                    </div>

                    {groups.map(group => {
                        const groupFeatures = activeFeatures.filter(f => f.group === group);
                        return (
                            <div key={group || 'main'} className="mb-4">
                                {group && <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{group}</p>}
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {groupFeatures.map(f => (
                                        <button key={f.key} onClick={() => toggleFeature(f.key)}
                                            className={`flex items-center gap-2 px-3 py-2 text-xs rounded-lg border transition-colors ${features[f.key] ? "border-green-300 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300" : "border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300"}`}>
                                            {features[f.key] ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                                            {f.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default ClientDetail;
